import type {
  DataGridProps,
  GridFilterItem,
  GridFilterModel,
  GridSortModel,
} from '@mui/x-data-grid';
import { useCallback, useState } from 'react';

interface GridView {
  filterModel?: GridFilterModel;
  sortModel?: GridSortModel;
  /** The search box above the grid, for tabs that have one. */
  search?: string;
}

type InitialState = NonNullable<DataGridProps['initialState']>;

// In memory only, so a view lasts until the page is reloaded. Signing out
// reloads the page, so one user's view never reaches the next.
const views = new Map<string, GridView>();

/** Forgets every remembered view (used by tests). */
export function clearGridViews(): void {
  views.clear();
}

const hasValue = (item: GridFilterItem) =>
  item.value !== undefined && item.value !== null && item.value !== '';

/**
 * Remembers a Manager Tab grid's filter and sort while the app is open.
 *
 * A sub-tab unmounts when you switch away from it, and the whole Manager Tab
 * unmounts when you leave its section, which would otherwise throw the grid's
 * state away. The view is read once, when the grid mounts, and written back on
 * every change. The page and the row selection are not remembered, so you come
 * back to the first page of fresh data.
 */
export function useGridView(key: string) {
  const [{ restored, initialState }] = useState(() => {
    const remembered = views.get(key) ?? {};
    const state: InitialState = {};
    if (remembered.filterModel) {
      state.filter = { filterModel: remembered.filterModel };
    }
    if (remembered.sortModel) {
      state.sorting = { sortModel: remembered.sortModel };
    }
    return { restored: remembered, initialState: state };
  });

  const save = useCallback(
    (patch: GridView) => {
      views.set(key, { ...views.get(key), ...patch });
    },
    [key],
  );
  // A filter that is still empty (the panel is open but nothing is typed yet)
  // is not worth remembering.
  const saveFilter = useCallback(
    (model: GridFilterModel) =>
      save({ filterModel: { ...model, items: model.items.filter(hasValue) } }),
    [save],
  );
  const saveSort = useCallback(
    (sortModel: GridSortModel) => save({ sortModel }),
    [save],
  );
  const saveSearch = useCallback((search: string) => save({ search }), [save]);

  return {
    /** What was remembered when the grid mounted. */
    restored,
    initialState,
    saveFilter,
    saveSort,
    saveSearch,
  };
}
